import React, {useEffect} from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Package, Pill, CheckCircle, AlertCircle } from 'lucide-react';
import { products } from '../mockData';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const ProductDetail = () => {
  const { id } = useParams();
  const product = products.find(p => p.id === id);

  useEffect(() => {
    if (product) {
      document.title = `${product.name} - Vetam Remedies`;
    }
    return () => {document.title= 'Vetam Remedies';};
  }, [product]);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h2>
          <Link to="/products">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white">
              Back to Products
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const relatedProducts = products
    .filter(p => p.category === product.category && p.id !== product.id)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-blue-600 hover:text-blue-700">Home</Link>
            <span className="text-gray-400">/</span>
            <Link to="/products" className="text-blue-600 hover:text-blue-700">Products</Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-600">{product.name}</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <Link to="/products" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-8 group">
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">Back to Products</span>
        </Link>

        <div className="grid lg:grid-cols-2 gap-12 mb-12">
          {/* Product Image */}
          <div>
            <Card className="overflow-hidden border-2">
              <CardContent className="p-0">
                <div className="relative h-96 bg-gradient-to-br from-blue-50 to-white">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-contain bg-white"
                  />
                  {product.featured && (
                    <div className="absolute top-6 right-6">
                      <span className="bg-green-500 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                        Featured Product
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Quick Info Cards */}
            <div className="grid grid-cols-2 gap-4 mt-6">
              <Card className="border-2">
                <CardContent className="p-4">
                  <Package className="w-8 h-8 text-blue-600 mb-2" />
                  <div className="text-sm text-gray-600">Packaging</div>
                  <div className="font-semibold text-gray-900">{product.packaging}</div>
                </CardContent>
              </Card>
              <Card className="border-2">
                <CardContent className="p-4">
                  <Pill className="w-8 h-8 text-blue-600 mb-2" />
                  <div className="text-sm text-gray-600">Form</div>
                  <div className="font-semibold text-gray-900">{product.dosageForm}</div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Product Details */}
          <div>
            <div className="mb-4">
              <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold">
                {product.category}
              </span>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{product.name}</h1>
            
            <p className="text-xl text-blue-600 italic mb-6">"{product.tagline}"</p>

            <Card className="mb-6 border-2 border-blue-200 bg-blue-50">
              <CardContent className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-blue-600" />
                  Composition
                </h3>
                <p className="text-gray-700 leading-relaxed">{product.composition}</p>
              </CardContent>
            </Card>

            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-lg text-gray-900 mb-3">Key Benefits</h3>
                <div className="space-y-2">
                  {product.keyBenefits.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-8 p-6 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl text-white">
              <h3 className="text-lg font-semibold mb-2">Interested in this product?</h3>
              <p className="text-blue-100 mb-4">Contact us for pricing and availability</p>
              <div className="flex gap-3">
                <Link to="/contact" className="flex-1">
                  <Button className="w-full bg-white text-blue-600 hover:bg-gray-100">
                    Contact Us
                  </Button>
                </Link>
                <Link to="/become-partner" className="flex-1">
                  <Button className="w-full bg-blue-800 hover:bg-blue-900 text-white">
                    Become Partner
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Information */}
        <Card className="mb-12">
          <CardContent className="p-8">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="uses">
                <AccordionTrigger className="text-xl font-bold text-gray-900">
                  Indications & Uses
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid md:grid-cols-2 gap-4 pt-4">
                    {product.uses.map((use, index) => (
                      <div key={index} className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 font-medium">{use}</span>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="benefits">
                <AccordionTrigger className="text-xl font-bold text-gray-900">
                  Detailed Benefits
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pt-4">
                    {product.keyBenefits.map((benefit, index) => (
                      <div key={index} className="flex items-start gap-3 p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 leading-relaxed">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="composition">
                <AccordionTrigger className="text-xl font-bold text-gray-900">
                  Complete Composition
                </AccordionTrigger>
                <AccordionContent>
                  <div className="p-6 bg-gradient-to-br from-blue-50 to-white rounded-lg border-2 border-blue-200 mt-4">
                    <p className="text-gray-800 leading-relaxed text-lg">{product.composition}</p>
                    <div className="mt-4 p-4 bg-white rounded-lg">
                      <p className="text-sm text-gray-600">
                        <strong>Dosage Form:</strong> {product.dosageForm}
                      </p>
                      <p className="text-sm text-gray-600 mt-2">
                        <strong>Packaging:</strong> {product.packaging}
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="storage">
                <AccordionTrigger className="text-xl font-bold text-gray-900">
                  Storage & Precautions
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pt-4">
                    <div className="p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
                      <p className="text-gray-700 leading-relaxed">
                        <strong>Storage:</strong> Store protected from light. Temperature not exceeding 25°C.
                      </p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg border-l-4 border-red-500">
                      <p className="text-gray-700 leading-relaxed">
                        <strong>Precautions:</strong> Keep medicine out of reach of children. Schedule H Prescription Drug. Use as directed by physician.
                      </p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                      <p className="text-gray-700 leading-relaxed">
                        <strong>Quality Assurance:</strong> Manufactured under WHO-GMP certified facilities with stringent quality controls.
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Related Products</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <Link key={relatedProduct.id} to={`/products/${relatedProduct.id}`}>
                  <Card className="h-full hover:shadow-xl transition-all transform hover:-translate-y-2 border-2 hover:border-blue-500 group cursor-pointer bg-white">
                    <CardContent className="p-0">
                      <div className="relative h-48 overflow-hidden rounded-t-lg bg-gradient-to-br from-blue-50 to-white">
                        <img
                          src={relatedProduct.image}
                          alt={relatedProduct.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-6">
                        <div className="mb-2">
                          <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold">
                            {relatedProduct.category}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                          {relatedProduct.name}
                        </h3>
                        <p className="text-sm text-gray-500 mb-3 italic">"{relatedProduct.tagline}"</p>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
